import { Text, Stack, Group, Title } from "@mantine/core"
import { CountryLabel, HelpHover } from "@tfg/ui"
import { useTranslation } from "react-i18next"

import { useFindSupplierMapping } from "~/gen/api"
import { COUNTRIES } from "~/utils/countries"
import { useGetOperations } from "~/utils/operations"

type ProductionSitesInformationProps = {
  supplierEmail: string
}

const ProductionSitesInformation = ({
  supplierEmail,
}: ProductionSitesInformationProps) => {
  const { t } = useTranslation("organizationSettings")
  const operations = useGetOperations()
  const { data: supplierProductionSites } = useFindSupplierMapping({
    filters: { email: [supplierEmail] },
    pagination: {
      skip: 0,
      take: 1,
    },
  })
  const productionSites =
    supplierProductionSites?.data?.data?.[0]?.supplier?.organization
      ?.productionSites

  const activities = [
    ...new Set(
      productionSites?.flatMap((productionSite) =>
        productionSite?.operations?.map(
          (operation) => operations[operation.activity?.name]?.name
        )
      )
    ),
  ]

  return (
    <Stack gap="sm" w="100%" p="xs">
      <Title order={3}>{t("supplierSettings.productionSites.title")}</Title>
      {!productionSites || productionSites?.length === 0 ? (
        <Text fw={500}>
          {t("supplierSettings.productionSites.noProductionSitesLabel")}
        </Text>
      ) : (
        productionSites?.map((productionSite) => (
          <Group key={productionSite.id} w="100%" justify="space-between">
            <Text fw={500}>{productionSite.name}</Text>
            <Group wrap="nowrap">
              <CountryLabel
                countryIso3166={productionSite.countryIso3166}
                countriesMapper={COUNTRIES}
                inline
              />
              {activities.length > 0 && (
                <HelpHover>
                  <Text>{`${t("supplierSettings.productionSites.productionOperations")}: ${activities.join(", ")}`}</Text>
                </HelpHover>
              )}
            </Group>
          </Group>
        ))
      )}
    </Stack>
  )
}

export default ProductionSitesInformation
